import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Container, ArrowLink } from "@/components/shared/PageHeader";
import { ImageGallery } from "@/components/shared/ImageGallery";
import { PriceTable } from "@/components/shared/PriceTable";
import { StatusBadge, EmptyState } from "@/components/shared/Status";
import { fetchRoom, fetchContent, fetchSite } from "@/lib/api";
import { floorLabel, fmtPrice, minPrice, paragraphs } from "@/lib/format";
import { Seo, roomJsonLd } from "@/components/shared/Seo";

const Fact = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <div className="t-mono text-muted-foreground">{label}</div>
    <div className="mt-1.5 text-[17px] font-medium tabular">{children}</div>
  </div>
);

/* Framing + alt text for lead photographs that need it (display only – the pictures themselves come from the database). */
const LEAD = {
  "kippers-kafe": { alt: "Kippers Kafé – fargerik retrokafé med vinylplater i taket", className: "[object-position:50%_32%]" },
};

export default function RoomDetail() {
  const { slug } = useParams();
  const { data: room, isLoading, isError } = useQuery({ queryKey: ["room", slug], queryFn: () => fetchRoom(slug) });
  const { data: content } = useQuery({ queryKey: ["content"], queryFn: fetchContent });
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSite });
  const lead = LEAD[slug];

  if (isLoading) return <Container className="py-12"><Skeleton className="h-8 w-64" /><Skeleton className="mt-6 aspect-[16/9] w-full rounded-[12px]" /></Container>;
  if (isError || !room) return <Container className="py-20"><EmptyState title="Fann ikkje rommet" text="Rommet du leitar etter finst ikkje." action={<Button asChild><Link to="/rom">Til alle rom</Link></Button>} /></Container>;

  const price = minPrice(room);
  const note = `Prisliste pr. ${site?.price_list_date || "13.03.2026"}. Dersom ikkje anna er merka, er prisane eksklusive vask, tilrigging og rydding.`;
  const info = content?.booking_info;

  return (
    <div data-testid="room-detail-page">
      <Seo
        title={room.name}
        description={`${room.tagline ? `${room.tagline}. ` : ""}${room.capacity ? `Plass til ${room.capacity} personar` : room.capacity_note || ""}${price ? `, leige frå ${fmtPrice(price)}` : ""}. ${room.description || ""}`}
        image={room.images?.[0]}
        imageAlt={room.captions?.[room.images?.[0]] || lead?.alt || `${room.name} – Halsnøy Kulturhus`}
        og={{ kind: "rom", id: room.slug }}
        jsonLd={roomJsonLd(room, site)}
      />
      <section>
        <Container className="pb-8 pt-8 sm:pt-10 lg:pb-10">
          <Link to="/rom" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid="room-back-link"><ArrowLeft className="h-4 w-4" /> Alle rom</Link>
          <div className="mt-8 grid gap-8 lg:grid-cols-12 lg:gap-8">
            <div className="lg:col-span-8">
              <div className="flex flex-wrap items-center gap-3">
                <span className="t-mono text-muted-foreground">{floorLabel(room.floor)}</span>
                <StatusBadge status={room.status} data-testid="room-status-badge" />
              </div>
              <h1 className="t-h1 mt-4 text-balance" data-testid="room-title">{room.name}</h1>
              {room.tagline && <p className="t-lead mt-6 max-w-2xl text-muted-foreground text-pretty">{room.tagline}</p>}
            </div>
            <div className="flex flex-col justify-end gap-3 lg:col-span-4 lg:items-end">
              {room.bookable ? (
                <Button asChild size="lg" className="w-full sm:w-auto" data-testid="room-book-button"><Link to={`/leig-rom?rom=${room.id}`}>Send bookingførespurnad <ArrowUpRight className="h-4 w-4" /></Link></Button>
              ) : (
                <p className="rounded-full border border-amber-600/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-950" data-testid="room-closed-note">Rommet er {room.status_note?.toLowerCase() || "stengt"} og kan ikkje bookast no.</p>
              )}
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto" data-testid="room-calendar-button"><Link to={`/kalender?rom=${room.id}`}>Sjå ledige datoar</Link></Button>
            </div>
          </div>
        </Container>
      </section>

      <Container className="pt-10 sm:pt-12">
        <ImageGallery images={room.images} captions={room.captions || {}} alt={room.name} leadAlt={lead?.alt} leadClassName={lead?.className} testId="room-gallery" />
        <dl className="mt-10 grid grid-cols-2 gap-x-8 gap-y-8 border-t border-border pt-8 md:grid-cols-4">
          <Fact label="Etasje">{floorLabel(room.floor)}</Fact>
          <Fact label="Kapasitet">{room.capacity ? `${room.capacity} personar` : room.capacity_note || "–"}</Fact>
          <Fact label="Pris frå">{price ? fmtPrice(price) : room.status === "closed" ? "Stengt" : "Etter avtale"}</Fact>
          <Fact label="Status">{room.status === "closed" ? room.status_note || "Mellombels stengt" : "Open for booking"}</Fact>
        </dl>
      </Container>

      <Container className="grid gap-12 py-14 sm:py-16 lg:grid-cols-12 lg:gap-8 lg:py-20">
        <div className="lg:col-span-7">
          <p className="t-mono text-muted-foreground">Om rommet</p>
          <p className="mt-4 font-display text-[clamp(22px,2vw,30px)] font-medium leading-[1.25] tracking-[-0.02em] text-pretty" data-testid="room-description">{room.description}</p>
          {room.features?.length > 0 && (
            <ul className="mt-8 grid gap-2 sm:grid-cols-2" data-testid="room-features">
              {room.features.map((f) => <li key={f} className="flex items-start gap-3 py-1.5 text-[15px]"><Check className="mt-1 h-4 w-4 shrink-0 text-brand" />{f}</li>)}
            </ul>
          )}
        </div>
      </Container>

      <Container className="grid gap-12 pb-6 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <p className="t-mono text-muted-foreground">Prisar</p>
          <h2 className="t-h2 mt-4">Husleigesatsar for {room.short_name || room.name}</h2>
          <div className="mt-8 border-t border-border"><PriceTable lines={room.price_lines} note={room.price_lines?.length ? note : room.price_note} testId="room-price-table" /></div>
        </div>
        {info && (
          <aside className="lg:col-span-4 lg:col-start-9" data-testid="room-booking-info">
            <h3 className="t-h3">{info.title}</h3>
            <div className="prose-nn mt-4 text-[15px] leading-relaxed text-muted-foreground">{paragraphs(info.body).map((p, i) => <p key={i}>{p}</p>)}</div>
            <div className="mt-5"><ArrowLink to="/prisar">Sjå heile prislista</ArrowLink></div>
          </aside>
        )}
      </Container>
    </div>
  );
}
